import { useState, useCallback } from 'react';
import { addDays } from 'date-fns';
import useApiFetch from '@/hooks/useApiFetch';
import chartsServices from '@/services/charts';
import { FetchApiIds } from '@/types';
import { defaultChartPeriod, today } from '@/utils/chart';
import OriginChartComponent from './OriginChart.component';

const OriginChartContainer = function (): JSX.Element {
  const [selectedFilter, setSelectedFilter] = useState(today);
  const periodOptions = defaultChartPeriod(selectedFilter);

  const onSelectFilter = useCallback((value: string): void => {
    setSelectedFilter(value);
  }, []);

  const sourcedOptions = [
    'Direct Approach on LinkedIn',
    'LinkedIn Limited Listings',
    'Consultancy',
    'DevHunt',
    'Email sent by Bot',
  ];
  const appliedOptions = [
    'LinkedIn Job Application',
    'LinkedIn',
    'Trio Website Careers Page',
    'Unknown',
    'Added by User',
  ];
  const referredOptions = ['Employee Referral', 'External Referral'];

  const originSources = [
    { name: 'Sourced', color: '#054AD9', options: sourcedOptions },
    { name: 'Applied', color: '#A8BFF2', options: appliedOptions },
    { name: 'Referred', color: '#001b4b', options: referredOptions },
  ];

  const candidatesFilteredResponse = useApiFetch(
    [FetchApiIds.ORIGIN_CHART, selectedFilter],
    async () => {
      const normalizedStartDate = new Date(selectedFilter);
      const endDatePlusOne = addDays(new Date(today), 1);
      normalizedStartDate.setHours(0, 0, 0, 0);
      return chartsServices.getSourceTotalByStatusAndPeriod({
        status: 'NEW,PENDING',
        startDate: normalizedStartDate.toISOString(),
        endDate: endDatePlusOne.toISOString(),
      });
    }
  );

  const formattedValues = originSources.map((status) => {
    const candidatesByStatus = candidatesFilteredResponse.data ?? {};

    const totalPerGroup = status.options.reduce((acc, cur) => {
      const amountPerStatus = candidatesByStatus[cur] ?? 0;
      return acc + amountPerStatus;
    }, 0);

    return {
      name: `${totalPerGroup} ${status.name}`,
      value: totalPerGroup,
    };
  });

  const totalOfCandidates = formattedValues.reduce((acc, cur) => {
    return acc + cur.value;
  }, 0);

  const originColors = originSources.map((status) => status.color);

  return (
    <OriginChartComponent
      isLoading={candidatesFilteredResponse.isLoading}
      onSelectFilter={onSelectFilter}
      periodOptions={periodOptions}
      title="Origin"
      data={formattedValues}
      total={totalOfCandidates}
      colors={originColors}
    />
  );
};

export default OriginChartContainer;
